import type { SocialPublishPlatform, StoredSocialPublishAttribution } from '@/types/social-publishing';

const STORAGE_KEY = 'octo.social-publishing-attribution';
const DEFAULT_ATTRIBUTION_TTL_SECONDS = 30 * 24 * 60 * 60;

const TRACKING_PARAM_KEYS = [
  'dd_social_token',
  'dd_social_visitor',
  'dd_social_platform',
  'dd_social_campaign_id',
  'dd_social_publish_id',
  'dd_social_asset_id',
  'dd_social_task_id',
] as const;

function normalizeParam(value: string | null): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function normalizePlatform(value: string | null): SocialPublishPlatform | null {
  return value === 'douyin' || value === 'xiaohongshu' || value === 'instagram' || value === 'tiktok' || value === 'x' || value === 'reddit' ? value : null;
}

function hasAttributionSignal(attribution: Omit<StoredSocialPublishAttribution, 'savedAt' | 'expiresAt'>): boolean {
  return Boolean(attribution.trackingToken || attribution.campaignId || attribution.publishJobId || attribution.assetId || attribution.taskId);
}

function saveStoredSocialPublishAttribution(
  input: Omit<StoredSocialPublishAttribution, 'savedAt' | 'expiresAt'> & { attributionTtlSeconds?: number },
): StoredSocialPublishAttribution | null {
  if (typeof window === 'undefined' || !hasAttributionSignal(input)) return null;

  const now = Date.now();
  const nextValue: StoredSocialPublishAttribution = {
    trackingToken: input.trackingToken ?? null,
    visitorKey: input.visitorKey ?? null,
    platform: input.platform ?? null,
    campaignId: input.campaignId ?? null,
    publishJobId: input.publishJobId ?? null,
    assetId: input.assetId ?? null,
    taskId: input.taskId ?? null,
    landingPath: input.landingPath ?? null,
    savedAt: now,
    expiresAt: now + (input.attributionTtlSeconds ?? DEFAULT_ATTRIBUTION_TTL_SECONDS) * 1000,
  };

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextValue));
  return nextValue;
}

export function captureSocialPublishAttributionFromUrl(searchParams: URLSearchParams, landingPath?: string | null): StoredSocialPublishAttribution | null {
  const attribution = {
    trackingToken: normalizeParam(searchParams.get('dd_social_token')),
    visitorKey: normalizeParam(searchParams.get('dd_social_visitor')),
    platform: normalizePlatform(searchParams.get('dd_social_platform')),
    campaignId: normalizeParam(searchParams.get('dd_social_campaign_id')),
    publishJobId: normalizeParam(searchParams.get('dd_social_publish_id')),
    assetId: normalizeParam(searchParams.get('dd_social_asset_id')),
    taskId: normalizeParam(searchParams.get('dd_social_task_id')),
    landingPath: landingPath ?? null,
  };
  return hasAttributionSignal(attribution) ? saveStoredSocialPublishAttribution(attribution) : null;
}

export function removeSocialPublishTrackingParamsFromUrl(url: URL): string {
  TRACKING_PARAM_KEYS.forEach((key) => url.searchParams.delete(key));
  return `${url.pathname}${url.search}${url.hash}`;
}
